import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    Box, 
    Typography,
    TextField, 
    Button,  
    CircularProgress, 
    FormControlLabel, 
    Checkbox, 
    MenuItem,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const JobTitlePage = () => {
    useAuthCheck();
    const { jobTitleId } = useParams();    
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formValues, setFormValues] = useState({
        jobTitleCode: '',
        description: '',
        jobTitleType: '',
        jobTitlePriority: 0,
        isActive: true,
    });
    
    
    useEffect(() => {     
        const fetchJobTitle = async () => {
            try {
                setLoading(true);

                if (jobTitleId) {
                    const res = await api.get(`/jobtitles/${jobTitleId}`);

                    setFormValues({
                        jobTitleCode: res.data.jobTitleCode || '',
                        description: res.data.description || '',
                        jobTitleType: res.data.jobTitleType || '',
                        jobTitlePriority: res.data.jobTitlePriority || 0,
                        isActive: res.data.isActive !== undefined ? res.data.isActive : true,
                    });
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchJobTitle();
    }, [jobTitleId]);
    
    const checkoutSchema = yup.object().shape({
        jobTitleCode: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = jobTitleId ? "put" : "post";
            const url = jobTitleId 
                ? `/jobtitles/update/${jobTitleId}` 
                : '/jobtitles/new';

            await api({
                method,
                url,
                data: {
                    jobTitleCode: values.jobTitleCode,
                    description: values.description,
                    jobTitleType: values.jobTitleType,
                    jobTitlePriority: values.jobTitlePriority,
                    isActive: values.isActive,
                },
            });

            toast.success(jobTitleId ? "Job title updated successfully" : "Job title created successfully");

            // onSubmitProps.resetForm(); 
            navigate("/jobtitles");
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to submit job title!");
            setError(error);
        }
    };

    
    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Job Title"
                    subtitle={`${loading ? 'Loading...' : jobTitleId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/jobtitles")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                >
                                    <TextField
                                        label="Job Title Code"
                                        name="jobTitleCode"
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.jobTitleCode}
                                        error={!!touched.jobTitleCode && !!errors.jobTitleCode}
                                        helperText={touched.jobTitleCode && errors.jobTitleCode}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        rows={2}
                                        label="Description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        name="description"
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />
                                    
                                    <TextField
                                        label="Job Title Type"
                                        name="jobTitleType"
                                        select
                                        variant="filled"
                                        value={values.jobTitleType}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        helperText={touched.jobTitleType && errors.jobTitleType}
                                        error={!!touched.jobTitleType && !!errors.jobTitleType}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                    >
                                        {[
                                            { value: "BOM", label: "Board of Management" },
                                            { value: "BOD", label: "Board of Directors" },
                                            { value: "Director", label: "Director" },
                                            { value: "Manager", label: "Manager" },
                                            { value: "Crew", label: "Crew" },
                                            { value: "Engineer", label: "Engineer" },
                                            { value: "Executive", label: "Executive" },
                                            { value: "Officer", label: "Officer" },
                                            { value: "Other", label: "Other" },
                                        ].map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}                                            
                                    </TextField>
                                    <TextField
                                        label="Job Title Priority"
                                        name="jobTitlePriority"
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.jobTitlePriority}
                                        error={!!touched.jobTitlePriority && !!errors.jobTitlePriority}
                                        helperText={touched.jobTitlePriority && errors.jobTitlePriority}
                                        slotProps={{
                                            inputProps: { min: 0, style: { textAlign: 'right' } } 
                                        }}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                    />

                                    <FormControlLabel
                                        label="Is Active"
                                        control={
                                            <Checkbox
                                            checked={values.isActive}
                                            onChange={handleChange}
                                            name="isActive"
                                            color="primary"
                                            />
                                        }
                                        sx={{ gridColumn: 'span 12' }}
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {jobTitleId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default JobTitlePage;
